import React, { useEffect, useRef, useState } from 'react';
import { Printer } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { formatCurrency, formatEGP, roundCurrency, getImageUrl, resolveLogoForReceiptPrint } from '../../utils/format';
import { computeOrderTotals } from '../../utils/orderPricing';
import { printHtml } from '../../utils/printing';
import { useLanguage } from '../../contexts/LanguageContext';
import en from '../../translations/en';
import ar from '../../translations/ar';

const translations = { en, ar };

// Get translation function for use outside React (e.g. in printReceipt)
const getT = () => {
  const lang = typeof localStorage !== 'undefined' ? (localStorage.getItem('language') || 'en') : 'en';
  const tr = translations[lang] || translations.en;
  return (key) => {
    const keys = key.split('.');
    let value = tr;
    for (const k of keys) value = value?.[k];
    return value || key;
  };
};

const defaultStoreName = 'RETAIL CASHIER';

// Space between logo and store name (px)
const RECEIPT_LOGO_MARGIN_BOTTOM = 1;

/** Order receipt print typography — Arabic styling is ground truth for EN + AR. */
const ORDER_RECEIPT_FONT_LINK =
  '<link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400..1000&display=swap" rel="stylesheet">';
const ORDER_RECEIPT_FONT_FAMILY = "'Cairo', 'Courier New', monospace";
const ORDER_RECEIPT_WEIGHT_BODY = 650;
const ORDER_RECEIPT_WEIGHT_HEADING = 750;

const ORDER_RECEIPT_PRINT_CSS = `
  @page { size: 80mm auto; margin: 0; }
  body { font-family: ${ORDER_RECEIPT_FONT_FAMILY}; font-size: 10px; margin: 0; padding: 0; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; -webkit-font-smoothing: antialiased; word-break: break-word; overflow-wrap: break-word; }
  .receipt { width: 100%; box-sizing: border-box; padding: 4px 16px; }
  .header { text-align: center; margin-bottom: 10px; }
  .title { font-size: 12px; font-weight: ${ORDER_RECEIPT_WEIGHT_HEADING}; margin-bottom: 3px; }
  .subtitle { font-size: 9px; color: #000; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; }
  .divider { border-top: 1px dashed #000; margin: 6px 0; }
  .item { display: flex; justify-content: space-between; margin: 3px 0; font-size: 10px; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; }
  .item-name { flex: 1; min-width: 0; }
  .item-price { text-align: right; min-width: 4.5em; flex-shrink: 0; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; }
  body[dir="rtl"] .item-price { text-align: left; }
  .total { font-weight: ${ORDER_RECEIPT_WEIGHT_HEADING}; margin-top: 6px; }
  .payment-info { margin-top: 8px; font-size: 10px; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; }
  .footer { text-align: center; margin-top: 10px; font-size: 10px; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; }
  .delivery-block { margin: 6px 0; font-size: 10px; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; }
  .delivery-title { font-weight: ${ORDER_RECEIPT_WEIGHT_HEADING}; margin-bottom: 4px; }
  .delivery-line { margin: 2px 0; word-break: break-word; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; }
  .delivery-line strong { font-weight: ${ORDER_RECEIPT_WEIGHT_HEADING}; }
  .barcode-container { width: 100%; text-align: center; margin: 6px 0; }
  .barcode-container svg { max-width: 100%; height: auto; width: 100%; }
  .qr-container { width: 100%; text-align: center; margin: 6px 0; }
  .qr-container svg { width: 80px; height: 80px; display: inline-block; }
  @media print { body { margin: 0; padding: 0; } }
`;

function orderReceiptBodyAttrs(isRTL) {
  const typography = `font-family: ${ORDER_RECEIPT_FONT_FAMILY}; font-size: 10px; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35;`;
  return isRTL
    ? `dir="rtl" style="text-align: right; ${typography}"`
    : `dir="ltr" style="${typography}"`;
}

/** Session closing receipt typography — Arabic styling is ground truth for EN + AR. */
const SESSION_CLOSING_RECEIPT_PRINT_CSS = `
  @page { size: 80mm auto; margin: 0; }
  body { font-family: ${ORDER_RECEIPT_FONT_FAMILY}; font-size: 10px; margin: 0; padding: 0; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; -webkit-font-smoothing: antialiased; word-break: break-word; overflow-wrap: break-word; }
  .receipt { width: 100%; box-sizing: border-box; padding: 4px 16px; }
  .header { text-align: center; margin-bottom: 20px; }
  .title { font-size: 13px; font-weight: ${ORDER_RECEIPT_WEIGHT_HEADING}; margin-bottom: 5px; }
  .subtitle { font-size: 10px; color: #000; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; }
  .divider { border-top: 1px dashed #000; margin: 10px 0; }
  .item { display: flex; justify-content: space-between; margin: 5px 0; font-size: 10px; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35; }
  .item-label { flex: 1; min-width: 0; }
  .item-value { text-align: right; font-weight: ${ORDER_RECEIPT_WEIGHT_HEADING}; min-width: 4.5em; flex-shrink: 0; }
  body[dir="rtl"] .item-value { text-align: left; }
  .section-title { font-weight: ${ORDER_RECEIPT_WEIGHT_HEADING}; margin-top: 10px; margin-bottom: 5px; font-size: 10px; }
  @media print { body { margin: 0; padding: 0; } }
`;

function sessionClosingReceiptBodyAttrs(isRTL) {
  const typography = `font-family: ${ORDER_RECEIPT_FONT_FAMILY}; font-size: 10px; font-weight: ${ORDER_RECEIPT_WEIGHT_BODY}; line-height: 1.35;`;
  return isRTL
    ? `dir="rtl" style="text-align: right; ${typography}"`
    : `dir="ltr" style="${typography}"`;
}

/** Store fields needed for session receipt printing (logo + name). */
export function storeInfoForSessionPrint(data) {
  const d = data || {};
  const ls = parseInt(d.logo_size, 10);
  return {
    storeName: d.name || '',
    logoPath: d.logo_path || '',
    logoSize: Number.isFinite(ls) && ls >= 40 && ls <= 400 ? ls : 100,
  };
}

async function receiptLogoHtml(storeInfo) {
  const logoPath = storeInfo?.logoPath || '';
  if (!logoPath) return '';
  const src = await resolveLogoForReceiptPrint(logoPath);
  if (!src) return '';
  const logoWidthPx = resolveReceiptLogoWidthPx(storeInfo);
  return `<div class="logo" style="text-align: center; margin-bottom: ${RECEIPT_LOGO_MARGIN_BOTTOM}px;"><img src="${src}" alt="" style="width: ${logoWidthPx}px; height: auto; max-width: 100%; object-fit: contain;" /></div>`;
}

/** Receipt logo width from store settings; fallback 100 px. */
function resolveReceiptLogoWidthPx(storeInfo) {
  const raw = storeInfo?.logoSize ?? storeInfo?.logo_size;
  const w = parseInt(raw, 10);
  if (Number.isFinite(w) && w >= 40 && w <= 400) return w;
  return 100;
}

/** Parse "Region (50.00)" stored on orders.delivery_region into name + fee. */
function parseDeliveryRegionField(deliveryRegion) {
  if (deliveryRegion == null || deliveryRegion === '') return { region: null, fee: null };
  const str = String(deliveryRegion).trim();
  const match = str.match(/^(.+?)\s*\(([0-9,]+\.?[0-9]*)\)\s*$/);
  if (match) {
    const fee = parseFloat(match[2].replace(/,/g, ''));
    return { region: match[1].trim(), fee: Number.isFinite(fee) ? fee : null };
  }
  return { region: str, fee: null };
}

function socialPlatformLabel(t, platform) {
  if (platform === 'x') return t('settings.xTwitter');
  return t(`settings.${platform}`) || platform;
}

/** Ensure DB / API orders have the same fields as live checkout before printing. */
export function normalizeTransactionForReceipt(transaction) {
  if (!transaction) return transaction;
  const t = { ...transaction };

  const items = (t.items || []).map((item) => {
    const qty = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const totalPrice = item.total_price != null
      ? parseFloat(item.total_price)
      : roundCurrency(qty * unitPrice);
    return {
      ...item,
      item_name: item.item_name || item.name || item.product_name,
      name: item.name || item.item_name || item.product_name,
      product_name: item.product_name || item.item_name || item.name,
      quantity: qty,
      unit_price: unitPrice,
      total_price: totalPrice,
    };
  });
  t.items = items;

  if (t.items_total == null && items.length > 0) {
    t.items_total = roundCurrency(items.reduce((s, i) => s + (parseFloat(i.total_price) || 0), 0));
  }

  const parsedRegion = parseDeliveryRegionField(t.delivery_region);
  if (t.delivery_fee_amount == null && parsedRegion.fee != null) {
    t.delivery_fee_amount = parsedRegion.fee;
  }
  t.delivery_region_label = parsedRegion.region || t.delivery_region || null;

  const delivery = { ...(t.delivery || {}) };
  if (delivery.name == null && t.customer_name) delivery.name = t.customer_name;
  if (delivery.phone == null && t.customer_phone) delivery.phone = t.customer_phone;
  if (delivery.address == null && t.customer_address) delivery.address = t.customer_address;
  if (delivery.region == null && t.delivery_region_label) delivery.region = t.delivery_region_label;
  if (Object.keys(delivery).length > 0) t.delivery = delivery;

  const payments = Array.isArray(t.payments) ? t.payments : [];
  if (payments.length > 0) {
    if (t.payment_method === 'mixed') {
      for (const p of payments) {
        const amt = parseFloat(p.amount) || 0;
        const ref = p.transaction_ref ? String(p.transaction_ref).trim() : '';
        if (p.method === 'cash') t.cash_amount = (parseFloat(t.cash_amount) || 0) + amt;
        else if (p.method === 'card') {
          t.card_amount = (parseFloat(t.card_amount) || 0) + amt;
          if (ref) t.card_transaction_no = t.card_transaction_no || ref;
        } else if (p.method === 'instapay') {
          t.instapay_amount = (parseFloat(t.instapay_amount) || 0) + amt;
          if (ref) t.instapay_transaction_no = t.instapay_transaction_no || ref;
        } else if (p.method === 'wallet') {
          t.wallet_amount = (parseFloat(t.wallet_amount) || 0) + amt;
          if (ref) t.wallet_transaction_no = t.wallet_transaction_no || ref;
        }
      }
    } else {
      const p = payments[0];
      const amt = parseFloat(p.amount) || 0;
      const ref = p.transaction_ref ? String(p.transaction_ref).trim() : '';
      if (p.method === 'cash') t.cash_amount = amt;
      else if (p.method === 'card') {
        if (amt) t.card_amount = amt;
        if (ref) t.card_transaction_no = ref;
      } else if (p.method === 'instapay') {
        if (amt) t.instapay_amount = amt;
        if (ref) t.instapay_transaction_no = ref;
      } else if (p.method === 'wallet') {
        if (amt) t.wallet_amount = amt;
        if (ref) t.wallet_transaction_no = ref;
      }
    }
  }

  return t;
}

function formatReceiptDateTime(createdAt, lang) {
  const locale = lang === 'ar' ? 'ar-EG' : undefined;
  const date = createdAt ? new Date(createdAt) : new Date();
  if (Number.isNaN(date.getTime())) {
    const now = new Date();
    return `${now.toLocaleDateString(locale)} ${now.toLocaleTimeString(locale)}`;
  }
  return `${date.toLocaleDateString(locale)} ${date.toLocaleTimeString(locale)}`;
}

function receiptNum(v) {
  return v != null && !Number.isNaN(Number(v)) ? Number(v) : 0;
}

const SESSION_FINANCIAL_PRINT_CSS = `
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 10px; }
  .meta-cell { border: 1px solid #000; padding: 6px 8px; box-sizing: border-box; }
  .meta-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px; }
  .meta-value { font-size: 11px; font-weight: 700; line-height: 1.3; }
  .payment-box { border: 1px solid #000; margin-bottom: 10px; }
  .summary-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 8px; border-bottom: 1px solid #000; font-size: 11px; font-weight: 600; }
  .summary-row:last-child { border-bottom: none; }
  .summary-row.method-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
  .summary-row.muted { background: #eee; font-weight: 700; }
  .summary-row.diff-row { background: #000; color: #fff; font-weight: 700; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

function buildSessionFinancialSummaryHtml(session, t, fmt, lang) {
  const cashSales = receiptNum(session.expected_closing_cash);
  const cardSales = receiptNum(session.expected_closing_card);
  const instapaySales = receiptNum(session.expected_closing_instapay);
  const walletSales = receiptNum(session.expected_closing_wallet);
  const openingCash = receiptNum(session.opening_cash);
  const refundAmount = receiptNum(session.refund_amount);
  const totalSales = cashSales + cardSales + instapaySales + walletSales;
  const totalExpectedCash = openingCash + cashSales - refundAmount;
  const diff = receiptNum(session.difference_cash);

  const startLabel = lang === 'ar' ? 'البداية' : 'Start';
  const endLabel = lang === 'ar' ? 'النهاية' : 'End';
  const openingLabel = lang === 'ar' ? 'النقدي الافتتاحي' : 'Opening cash';

  const paymentCols = [
    { label: t('pos.cash'), amount: cashSales },
    { label: t('pos.card'), amount: cardSales },
    { label: t('pos.instapay'), amount: instapaySales },
    { label: t('pos.wallet'), amount: walletSales },
  ];

  const metaCells = [
    { label: startLabel, value: session.start_time ? formatReceiptDateTime(session.start_time, lang) : '—' },
    { label: endLabel, value: session.end_time ? formatReceiptDateTime(session.end_time, lang) : '—' },
    { label: openingLabel, value: fmt(session.opening_cash) },
    { label: t('sessions.closingCash'), value: session.closing_cash != null ? fmt(session.closing_cash) : '—' },
  ];

  return `
    <div class="meta-grid">
      ${metaCells.map(({ label, value }) => `
        <div class="meta-cell">
          <div class="meta-label">${label}</div>
          <div class="meta-value">${value}</div>
        </div>
      `).join('')}
    </div>

    <div class="payment-box">
      ${paymentCols.map(({ label, amount }) => `
        <div class="summary-row method-label">
          <span>${label}</span>
          <span>${fmt(amount)}</span>
        </div>
      `).join('')}
      <div class="summary-row muted">
        <span>${t('sessions.totalSales')}</span>
        <span>${fmt(totalSales)}</span>
      </div>
      <div class="summary-row">
        <span>${t('receipt.refundAmount')}</span>
        <span>${fmt(refundAmount)}</span>
      </div>
      <div class="summary-row muted">
        <span>${t('sessions.expectedCash')}</span>
        <span>${fmt(totalExpectedCash)}</span>
      </div>
      <div class="summary-row diff-row">
        <span>${t('sessions.difference')}</span>
        <span>${diff > 0 ? '(-) ' : diff < 0 ? '(+) ' : ''}${fmt(Math.abs(diff))}</span>
      </div>
    </div>
  `;
}

// Generate barcode SVG as string for printing
// Uses a base width that will be scaled by CSS to fit the receipt width
const generateBarcodeSVG = (receiptNum) => {
  if (!receiptNum || typeof document === 'undefined') return '';
  
  try {
    // Create a temporary SVG element
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    
    // Use a base width - CSS will scale it to fit the receipt width
    // The width value here is the thickness of each bar (smaller = more bars fit)
    JsBarcode(svg, receiptNum.toString(), {
      format: 'CODE128',
      width: 1.5, // Smaller width allows more bars, making it scale better
      height: 50,
      displayValue: true,
      fontSize: 12,
      margin: 0, // Remove margins so it can fill the width
      background: '#ffffff',
      lineColor: '#000000',
    });
    
    // Get the SVG as string and add style for responsive width
    const serializer = new XMLSerializer();
    let svgString = serializer.serializeToString(svg);
    
    // Add viewBox and preserveAspectRatio for better scaling
    // Replace the SVG tag to add responsive attributes
    svgString = svgString.replace(
      /<svg([^>]*)>/,
      '<svg$1 style="max-width: 100%; width: 100%; height: auto;" preserveAspectRatio="xMidYMid meet">'
    );
    
    return svgString;
  } catch (error) {
    console.error('Error generating barcode:', error);
    return '';
  }
};

export const printReceipt = async (transaction, storeInfo = {}) => {
  const lang = typeof localStorage !== 'undefined' ? (localStorage.getItem('language') || 'en') : 'en';
  const t = getT();
  transaction = normalizeTransactionForReceipt(transaction);
  const defaultFooter = `${t('receipt.thankYou')}\n${t('receipt.visitAgain')}`;
  const storeName = storeInfo.storeName || transaction.store_name || defaultStoreName;
  const receiptFooter = storeInfo.receiptFooter ?? transaction.receipt_footer ?? defaultFooter;
  const cashierName = transaction.cashier_name || t('settings.cashier');
  const phoneNumbersDisplay = storeInfo.phoneNumbers && storeInfo.phoneNumbers.length > 0 ? storeInfo.phoneNumbers.join(' - ') : '';
  const socialMediaUrls = storeInfo.socialMediaUrls || [];
  const socialMediaDisplay = socialMediaUrls.length > 0
    ? socialMediaUrls.map((s) => `${socialPlatformLabel(t, s.platform)}: ${s.url}`).join('\n')
    : '';
  const receiptDateTime = formatReceiptDateTime(transaction.created_at, lang);
  const deliveryRegionLabel = transaction.delivery_region_label || transaction.delivery_region;
  const websiteConfig = storeInfo.website;
  const websiteLinkDisplay = (websiteConfig?.include_in_receipt === 'link' || websiteConfig?.include_in_receipt === 'qr') && websiteConfig?.url
    ? websiteConfig.url
    : '';
  const logoHtml = await receiptLogoHtml(storeInfo);
  const includeDeliveryFee = storeInfo.includeDeliveryFee !== false;
  const hidePaymentSection = !includeDeliveryFee && transaction.delivery_fee_amount != null && transaction.delivery_region;
  const isRTL = lang === 'ar';
  const qtyPriceFormat = (qty, price) => isRTL ? `${formatEGP(price)} × ${qty}` : `${qty} × ${formatEGP(price)}`;
  const itemsHtml = (transaction.items || [])
    .map((item) => `
      <div class="item">
        <div class="item-name">
          ${item.item_name || item.name || item.product_name}
          <br />
          <span style="font-size: 10px" dir="${isRTL ? 'rtl' : 'ltr'}">${qtyPriceFormat(item.quantity, item.unit_price)}</span>
        </div>
        <div class="item-price">${formatEGP(item.total_price || roundCurrency(item.quantity * item.unit_price))}</div>
      </div>
    `)
    .join('');

  let paymentDetails = '';
  if (!hidePaymentSection) {
    paymentDetails = `<div class="item"><span>${t('receipt.paymentMethod')}:</span><span>${(transaction.payment_method || '').toUpperCase()}</span></div>`;

  if (transaction.payment_method === 'mixed') {
    if (transaction.cash_amount) paymentDetails += `<div class="item"><span>${t('receipt.cashAmount')}:</span><span>${formatEGP(transaction.cash_amount)}</span></div>`;
    if (transaction.card_amount) paymentDetails += `<div class="item"><span>${t('receipt.cardAmount')}:</span><span>${formatEGP(transaction.card_amount)}</span></div>`;
    if (transaction.instapay_amount) paymentDetails += `<div class="item"><span>${t('receipt.instapayAmount')}:</span><span>${formatEGP(transaction.instapay_amount)}</span></div>`;
    if (transaction.wallet_amount) paymentDetails += `<div class="item"><span>${t('receipt.walletAmount')}:</span><span>${formatEGP(transaction.wallet_amount)}</span></div>`;
  } else if (transaction.cash_amount) {
    paymentDetails += `<div class="item"><span>${t('receipt.cashReceived')}:</span><span>${formatEGP(transaction.cash_amount)}</span></div>`;
    paymentDetails += `<div class="item"><span>${t('receipt.change')}:</span><span>${formatEGP(roundCurrency(parseFloat(transaction.cash_amount) - parseFloat(transaction.total_amount)))}</span></div>`;
  }
  
  if (transaction.card_transaction_no) paymentDetails += `<div class="item"><span>${t('receipt.cardTransactionNo')}:</span><span>${transaction.card_transaction_no}</span></div>`;
  if (transaction.instapay_transaction_no) paymentDetails += `<div class="item"><span>${t('receipt.instapayTransactionNo')}:</span><span>${transaction.instapay_transaction_no}</span></div>`;
  if (transaction.wallet_transaction_no) paymentDetails += `<div class="item"><span>${t('receipt.walletTransactionNo')}:</span><span>${transaction.wallet_transaction_no}</span></div>`;
  }

  const isDelivery = transaction.delivery_region || (transaction.delivery && (transaction.delivery.phone || transaction.delivery.name || transaction.delivery.address));
  const itemsTotalForTax = transaction.items_total ?? (transaction.items || []).reduce((s, i) => s + (i.total_price ?? i.quantity * i.unit_price), 0);
  const receiptPricing = computeOrderTotals({
    itemsSubtotal: itemsTotalForTax,
    promoPct: transaction.promo_percentage,
    discountPct: transaction.discount_percentage,
    taxPct: transaction.tax_percentage,
    deliveryFee: 0,
    includeDeliveryInTotal: false,
  });
  const computedTaxAmount = (transaction.tax_percentage || 0) > 0 && transaction.tax_amount == null
    ? receiptPricing.taxAmount
    : (transaction.tax_amount ?? 0);
  const promoPct = parseFloat(transaction.promo_percentage) || 0;
  const promoLabel = transaction.promo_name || t('pos.promo');
  const d = transaction.delivery || {};
  const receiptDisplayTotal = !includeDeliveryFee && transaction.delivery_fee_amount != null && transaction.delivery_region
    ? roundCurrency(parseFloat(transaction.total_amount || 0) + parseFloat(transaction.delivery_fee_amount || 0))
    : transaction.total_amount;
  const deliveryBlockHtml = isDelivery
    ? `
          <div class="delivery-block">
            <div class="delivery-title">${t('pos.delivery').toUpperCase()}</div>
            ${d.name ? `<div class="delivery-line"><strong>${t('pos.customerName')}:</strong> ${d.name}</div>` : ''}
            ${d.phone ? `<div class="delivery-line"><strong>${t('pos.customerPhone')}:</strong> ${d.phone}</div>` : ''}
            ${d.address ? `<div class="delivery-line"><strong>${t('pos.customerAddress')}:</strong> ${d.address}</div>` : ''}
            ${(deliveryRegionLabel || d.region) ? `<div class="delivery-line"><strong>${t('pos.deliveryRegion')}:</strong> ${deliveryRegionLabel || d.region}</div>` : ''}
            ${(transaction.delivery_person_name || d.delivery_person_name) ? `<div class="delivery-line"><strong>${t('pos.deliveryPerson')}:</strong> ${transaction.delivery_person_name || d.delivery_person_name}</div>` : ''}
          </div>
          <div class="divider"></div>`
    : '';

  // Generate barcode for receipt_num
  const receiptNum = transaction.receipt_num || transaction.id || '';
  const barcodeHtml = receiptNum ? `
    <div class="barcode-container" style="text-align: center; margin: 10px 0; width: 100%;">
      ${generateBarcodeSVG(receiptNum)}
    </div>
  ` : '';

  // Generate website QR code when include_in_receipt is 'qr' (order receipt only)
  let websiteQRHtml = '';
  if (websiteConfig?.include_in_receipt === 'qr' && websiteConfig?.url) {
    try {
      const qrSvg = await QRCode.toString(websiteConfig.url, { type: 'svg', width: 150, margin: 1 });
      websiteQRHtml = `
    <div class="qr-container" style="text-align: center; margin: 10px 0; width: 100%;">
      <div style="font-size: 10px; margin-bottom: 4px;">${t('receipt.visitUsOn')}</div>
      ${qrSvg}
    </div>
  `;
    } catch (err) {
      console.error('Failed to generate website QR code:', err);
    }
  }

  const html = `
    <html>
      <head>
        <title>Receipt</title>
        ${ORDER_RECEIPT_FONT_LINK}
        <style>${ORDER_RECEIPT_PRINT_CSS}</style>
      </head>
      <body ${orderReceiptBodyAttrs(isRTL)}>
        <div class="receipt">
          <div class="header">
            ${logoHtml}
            <div class="title">${storeName}</div>
            ${phoneNumbersDisplay ? `<div class="subtitle">${phoneNumbersDisplay}</div>` : ''}
            ${websiteLinkDisplay ? `<div class="subtitle">${t('receipt.visitUsOn')}: ${websiteLinkDisplay}</div>` : ''}
            ${socialMediaDisplay ? `<div class="subtitle" style="font-size: 10px;">${socialMediaDisplay.split('\n').map((line) => `<div>${line}</div>`).join('')}</div>` : ''}
            <div class="subtitle">${receiptDateTime}</div>
            <div class="subtitle">${t('receipt.cashier')}: ${cashierName}</div>
          </div>
          <div class="divider"></div>
          ${deliveryBlockHtml}
          <div class="items">${itemsHtml}</div>
          <div class="divider"></div>
          <div class="total">
            ${
              transaction.items_total !== undefined || (transaction.delivery_fee_amount !== undefined && transaction.delivery_region)
                ? `
            ${transaction.items_total !== undefined ? `<div class="item"><span>${t('receipt.subtotal')}:</span><span>${formatEGP(transaction.items_total)}</span></div>` : ''}
            ${promoPct > 0 ? `<div class="item"><span>${promoLabel} (${promoPct}%):</span><span>-${formatEGP(receiptPricing.promoAmount)}</span></div>` : ''}
            ${(transaction.discount_percentage || 0) > 0 ? `<div class="item"><span>${t('receipt.discount')} (${transaction.discount_percentage}%):</span><span>-${formatEGP(receiptPricing.discountAmount)}</span></div>` : ''}
            ${(transaction.tax_percentage || 0) > 0 ? `<div class="item"><span>${t('receipt.tax')} (${transaction.tax_percentage}%):</span><span>${formatEGP(computedTaxAmount)}</span></div>` : ''}
            ${transaction.delivery_fee_amount !== undefined && transaction.delivery_region ? `<div class="item"><span>${t('receipt.deliveryFee')} (${deliveryRegionLabel || transaction.delivery_region}):</span><span>${formatEGP(transaction.delivery_fee_amount || 0)}</span></div>` : ''}
            <div class="item"><span>${t('receipt.total')}:</span><span>${formatEGP(receiptDisplayTotal)}</span></div>
            `
                : `
            <div class="item"><span>${t('receipt.total')}:</span><span>${formatEGP(receiptDisplayTotal)}</span></div>
            `
            }
          </div>
          ${!hidePaymentSection ? `<div class="payment-info">${paymentDetails}</div>` : ''}
          <div class="divider"></div>
          ${websiteQRHtml}
          ${(websiteQRHtml && barcodeHtml) ? '<div class="divider"></div>' : ''}
          ${barcodeHtml}
          <div class="footer">
            ${receiptFooter.split('\n').map(line => `<div>${line}</div>`).join('')}
          </div>
        </div>
      </body>
    </html>
  `;

  await printHtml(html);
};

/**
 * Print a minimal refund receipt: logo, store name, date, cashier, refunded items
 * (name + quantity), total refunded amount, and the order receipt code as a barcode.
 *
 * @param {Object} refund
 * @param {string} refund.receipt_num   Original order receipt number (used for barcode)
 * @param {string} refund.cashier_name  Name of the cashier performing the refund
 * @param {string|Date} [refund.refunded_at]  Date of refund (defaults to now)
 * @param {Array<{ product_name?: string, name?: string, item_name?: string,
 *                 refunded_quantity?: number, quantity?: number,
 *                 product_unit_type?: string, unit_type?: string }>} refund.items
 * @param {number} refund.total         Total refunded amount
 * @param {Object} [storeInfo]          { storeName, logoPath, logoSize }
 */
export const printRefundReceipt = async (refund, storeInfo = {}) => {
  const lang = typeof localStorage !== 'undefined' ? (localStorage.getItem('language') || 'en') : 'en';
  const isRTL = lang === 'ar';
  const t = getT();

  const storeName = storeInfo.storeName || defaultStoreName;
  const cashierName = refund?.cashier_name || t('settings.cashier');
  const refundDateTime = formatReceiptDateTime(refund?.refunded_at, lang);
  const logoHtml = await receiptLogoHtml(storeInfo);

  const getUnitLabel = (raw) => {
    const u = String(raw || '').toLowerCase();
    if (!u || u === 'piece') return t('pos.pcs');
    if (u === 'kg' || u === 'weight') return t('pos.kg');
    return raw;
  };

  const formatQty = (item) => {
    const qty = parseFloat(item.refunded_quantity ?? item.quantity ?? 0);
    const qtyStr = Number.isFinite(qty) ? qty.toLocaleString() : '0';
    const unitRaw = item.product_unit_type ?? item.unit_type;
    return unitRaw == null || String(unitRaw).trim() === ''
      ? qtyStr
      : `${qtyStr} ${getUnitLabel(unitRaw)}`;
  };

  const items = Array.isArray(refund?.items) ? refund.items : [];
  const itemsHtml = items
    .map((item) => {
      const name = item.product_name || item.item_name || item.name || '';
      const qtyText = formatQty(item);
      const amount = parseFloat(item.refunded_amount ?? 0) || 0;
      return `
      <div class="item">
        <div class="item-name">
          ${name}
          <br />
          <span style="font-size: 10px">${qtyText}</span>
        </div>
        <div class="item-price">${formatEGP(amount)}</div>
      </div>`;
    })
    .join('');

  const totalAmount = parseFloat(refund?.total ?? 0) || 0;
  const receiptCode = refund?.receipt_num || '';
  const barcodeHtml = receiptCode
    ? `<div class="barcode-container" style="text-align: center; margin: 10px 0; width: 100%;">${generateBarcodeSVG(receiptCode)}</div>`
    : '';

  const html = `
    <html>
      <head>
        <title>${t('receipt.refundReceipt')}</title>
        ${ORDER_RECEIPT_FONT_LINK}
        <style>${ORDER_RECEIPT_PRINT_CSS}</style>
      </head>
      <body ${orderReceiptBodyAttrs(isRTL)}>
        <div class="receipt">
          <div class="header">
            ${logoHtml}
            <div class="title">${storeName}</div>
            <div class="subtitle" style="font-weight: ${ORDER_RECEIPT_WEIGHT_HEADING};">${t('receipt.refundReceipt')}</div>
            <div class="subtitle">${refundDateTime}</div>
            <div class="subtitle">${t('receipt.cashier')}: ${cashierName}</div>
          </div>
          <div class="divider"></div>
          <div class="items">${itemsHtml}</div>
          <div class="divider"></div>
          <div class="total">
            <div class="item"><span>${t('receipt.refundAmount')}:</span><span>${formatEGP(totalAmount)}</span></div>
          </div>
          ${barcodeHtml ? '<div class="divider"></div>' : ''}
          ${barcodeHtml}
        </div>
      </body>
    </html>
  `;

  await printHtml(html);
};

const Receipt = ({ transaction: transactionProp, storeName: storeNameProp, receiptFooter: receiptFooterProp, website: websiteProp, phoneNumbers: phoneNumbersProp, socialMediaUrls: socialMediaUrlsProp = [], logoPath: logoPathProp, logoSize: logoSizeProp, includeDeliveryFee: includeDeliveryFeeProp = true, onClose }) => {
  const { t, isRTL, language } = useLanguage();
  const transaction = normalizeTransactionForReceipt(transactionProp);
  const storeName = storeNameProp || transaction?.store_name || defaultStoreName;
  const [websiteQRDataUrl, setWebsiteQRDataUrl] = useState('');
  const showWebsiteQR = websiteProp?.include_in_receipt === 'qr' && websiteProp?.url;
  const showWebsiteLink = (websiteProp?.include_in_receipt === 'link' || websiteProp?.include_in_receipt === 'qr') && websiteProp?.url;
  const receiptFooter = receiptFooterProp ?? transaction?.receipt_footer ?? `${t('receipt.thankYou')}\n${t('receipt.visitAgain')}`;
  const cashierName = transaction?.cashier_name || t('settings.cashier');
  const phoneNumbersDisplay = phoneNumbersProp && phoneNumbersProp.length > 0 ? phoneNumbersProp.join(' - ') : '';
  const socialMediaDisplay = socialMediaUrlsProp.length > 0
    ? socialMediaUrlsProp.map((s) => `${socialPlatformLabel(t, s.platform)}: ${s.url}`).join('\n')
    : '';
  const logoPath = logoPathProp || '';
  const logoWidthPx = resolveReceiptLogoWidthPx({ logoSize: logoSizeProp });
  const deliveryRegionLabel = transaction?.delivery_region_label || transaction?.delivery_region;
  const isDelivery = transaction?.delivery_region || (transaction?.delivery && (transaction.delivery.phone || transaction.delivery.name || transaction.delivery.address));
  const delivery = transaction?.delivery || {};
  const receiptNum = transaction?.receipt_num || transaction?.id || '';
  const receiptDateTime = formatReceiptDateTime(transaction?.created_at, language);
  const itemsTotalForTax = transaction?.items_total ?? (transaction?.items || []).reduce((s, i) => s + (i.total_price ?? i.quantity * i.unit_price), 0);
  const receiptPricing = computeOrderTotals({
    itemsSubtotal: itemsTotalForTax,
    promoPct: transaction?.promo_percentage,
    discountPct: transaction?.discount_percentage,
    taxPct: transaction?.tax_percentage,
    deliveryFee: 0,
    includeDeliveryInTotal: false,
  });
  const computedTaxAmount = (transaction?.tax_percentage || 0) > 0 && transaction?.tax_amount == null
    ? receiptPricing.taxAmount
    : (transaction?.tax_amount ?? 0);
  const promoPct = parseFloat(transaction?.promo_percentage) || 0;
  const promoLabel = transaction?.promo_name || t('pos.promo');
  const receiptDisplayTotal = !includeDeliveryFeeProp && transaction?.delivery_fee_amount != null && transaction?.delivery_region
    ? roundCurrency(parseFloat(transaction?.total_amount || 0) + parseFloat(transaction?.delivery_fee_amount || 0))
    : (transaction?.total_amount ?? 0);
  const hidePaymentSection = !includeDeliveryFeeProp && transaction?.delivery_fee_amount != null && transaction?.delivery_region;
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current && receiptNum) {
      try {
        JsBarcode(barcodeRef.current, receiptNum.toString(), {
          format: 'CODE128',
          width: 1.5, // Smaller width for better scaling
          height: 50,
          displayValue: true,
          fontSize: 12,
          margin: 0, // Remove margins for full width
          background: '#ffffff',
          lineColor: '#000000',
        });
        // Set SVG attributes for responsive scaling
        if (barcodeRef.current) {
          barcodeRef.current.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        }
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [receiptNum]);

  useEffect(() => {
    if (showWebsiteQR && websiteProp?.url) {
      QRCode.toDataURL(websiteProp.url, { width: 120, margin: 1 })
        .then(setWebsiteQRDataUrl)
        .catch((err) => console.error('Failed to generate website QR:', err));
    } else {
      setWebsiteQRDataUrl('');
    }
  }, [showWebsiteQR, websiteProp?.url]);

  const handlePrint = () => {
    printReceipt(transaction, {
      storeName,
      receiptFooter,
      website: websiteProp,
      phoneNumbers: phoneNumbersProp,
      socialMediaUrls: socialMediaUrlsProp,
      logoPath,
      logoSize: logoSizeProp,
      includeDeliveryFee: includeDeliveryFeeProp,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-4 max-w-xs w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{t('receipt.receipt')}</h3>
        </div>

        <div id="receipt-content" className="receipt">
          <div className="header">
            {logoPath && (
              <div className="logo" style={{ textAlign: 'center', marginBottom: `${RECEIPT_LOGO_MARGIN_BOTTOM}px` }}>
                <img src={getImageUrl(logoPath)} alt="Logo" style={{ width: `${logoWidthPx}px`, height: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
              </div>
            )}
            <div className="title">{storeName}</div>
            {phoneNumbersDisplay && <div className="subtitle">{phoneNumbersDisplay}</div>}
            {showWebsiteLink && (
              <div className="subtitle">{t('receipt.visitUsOn')}: {websiteProp.url}</div>
            )}
            {socialMediaDisplay && (
              <div className="subtitle" style={{ fontSize: '10px' }}>
                {socialMediaDisplay.split('\n').map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            )}
            <div className="subtitle">{receiptDateTime}</div>
            <div className="subtitle">{t('receipt.cashier')}: {cashierName}</div>
          </div>

          <div className="divider"></div>

          {isDelivery && (
            <>
              <div className="mt-2 mb-2 text-xs">
                <div className="font-bold mb-1">{t('pos.delivery').toUpperCase()}</div>
                {delivery.name && <div><strong>{t('pos.customerName')}:</strong> {delivery.name}</div>}
                {delivery.phone && <div><strong>{t('pos.customerPhone')}:</strong> {delivery.phone}</div>}
                {delivery.address && <div className="break-words"><strong>{t('pos.customerAddress')}:</strong> {delivery.address}</div>}
                {(deliveryRegionLabel || delivery.region) && (
                  <div><strong>{t('pos.deliveryRegion')}:</strong> {deliveryRegionLabel || delivery.region}</div>
                )}
                {(transaction.delivery_person_name || delivery.delivery_person_name) && (
                  <div><strong>{t('pos.deliveryPerson')}:</strong> {transaction.delivery_person_name || delivery.delivery_person_name}</div>
                )}
              </div>
              <div className="divider"></div>
            </>
          )}

          <div className="items">
            {(transaction.items || []).map((item, index) => (
              <div key={index} className="item">
                <div className="item-name">
                  {item.item_name || item.name || item.product_name}
                  <br />
                  <span style={{ fontSize: '10px' }} dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? `${formatEGP(item.unit_price)} × ${item.quantity}` : `${item.quantity} × ${formatEGP(item.unit_price)}`}
                  </span>
                </div>
                <div className="item-price">
                  {formatEGP(item.total_price || roundCurrency(item.quantity * item.unit_price))}
                </div>
              </div>
            ))}
          </div>

          <div className="divider"></div>

          <div className="total">
            {transaction.items_total !== undefined || (transaction.delivery_fee_amount !== undefined && transaction.delivery_region) ? (
              <>
                {transaction.items_total !== undefined && (
                  <div className="item">
                    <span>{t('receipt.subtotal')}:</span>
                    <span>{formatEGP(transaction.items_total)}</span>
                  </div>
                )}
                {promoPct > 0 && (
                  <div className="item">
                    <span>{promoLabel} ({promoPct}%):</span>
                    <span>-{formatEGP(receiptPricing.promoAmount)}</span>
                  </div>
                )}
                {(transaction.discount_percentage || 0) > 0 && (
                  <div className="item">
                    <span>{t('receipt.discount')} ({transaction.discount_percentage}%):</span>
                    <span>-{formatEGP(receiptPricing.discountAmount)}</span>
                  </div>
                )}
                {transaction.discount_notes && (
                  <div className="item">
                    <span>{t('pos.discountNotes')}:</span>
                    <span>{transaction.discount_notes}</span>
                  </div>
                )}
                {transaction.discount_approver_name && (
                  <div className="item">
                    <span>{t('refunds.approvedBy')}:</span>
                    <span>{transaction.discount_approver_name}</span>
                  </div>
                )}
                {(transaction.tax_percentage || 0) > 0 && (
                  <div className="item">
                    <span>{t('receipt.tax')} ({transaction.tax_percentage}%):</span>
                    <span>{formatEGP(computedTaxAmount)}</span>
                  </div>
                )}
                {transaction.delivery_fee_amount !== undefined && transaction.delivery_region && (
                  <div className="item">
                    <span>{t('receipt.deliveryFee')} ({deliveryRegionLabel || transaction.delivery_region}):</span>
                    <span>{formatEGP(transaction.delivery_fee_amount || 0)}</span>
                  </div>
                )}
                <div className="item">
                  <span>{t('receipt.total')}:</span>
                  <span>{formatEGP(receiptDisplayTotal)}</span>
                </div>
              </>
            ) : (
              <div className="item">
                <span>{t('receipt.total')}:</span>
                <span>{formatEGP(receiptDisplayTotal)}</span>
              </div>
            )}
          </div>

          {!hidePaymentSection && (
          <div className="payment-info">
            <div className="item">
              <span>{t('receipt.paymentMethod')}:</span>
              <span>{transaction.payment_method?.toUpperCase()}</span>
            </div>

            {transaction.payment_method === 'mixed' ? (
              <>
                {transaction.cash_amount ? (
                  <div className="item">
                    <span>{t('receipt.cashAmount')}:</span>
                    <span>{formatEGP(transaction.cash_amount)}</span>
                  </div>
                ) : null}
                {transaction.card_amount ? (
                  <div className="item">
                    <span>{t('receipt.cardAmount')}:</span>
                    <span>{formatEGP(transaction.card_amount)}</span>
                  </div>
                ) : null}
                {transaction.instapay_amount ? (
                  <div className="item">
                    <span>{t('receipt.instapayAmount')}:</span>
                    <span>{formatEGP(transaction.instapay_amount)}</span>
                  </div>
                ) : null}
                {transaction.wallet_amount ? (
                  <div className="item">
                    <span>{t('receipt.walletAmount')}:</span>
                    <span>{formatEGP(transaction.wallet_amount)}</span>
                  </div>
                ) : null}
              </>
            ) : transaction.cash_amount ? (
              <>
                <div className="item">
                  <span>{t('receipt.cashReceived')}:</span>
                  <span>{formatEGP(transaction.cash_amount)}</span>
                </div>
                <div className="item">
                  <span>{t('receipt.change')}:</span>
                  <span>{formatEGP(roundCurrency(parseFloat(transaction.cash_amount) - parseFloat(transaction.total_amount)))}</span>
                </div>
              </>
            ) : null}

            {transaction.card_transaction_no && (
              <div className="item">
                <span>{t('receipt.cardTransactionNo')}:</span>
                <span>{transaction.card_transaction_no}</span>
              </div>
            )}
            {transaction.instapay_transaction_no && (
              <div className="item">
                <span>{t('receipt.instapayTransactionNo')}:</span>
                <span>{transaction.instapay_transaction_no}</span>
              </div>
            )}
            {transaction.wallet_transaction_no && (
              <div className="item">
                <span>{t('receipt.walletTransactionNo')}:</span>
                <span>{transaction.wallet_transaction_no}</span>
              </div>
            )}
          </div>
          )}

          <div className="divider"></div>

          {showWebsiteQR && websiteQRDataUrl && (
            <div className="text-center my-4">
              <div className="text-xs mb-1">{t('receipt.visitUsOn')}</div>
              <img src={websiteQRDataUrl} alt="Website QR" style={{ width: 120, height: 120 }} />
            </div>
          )}

          {showWebsiteQR && websiteQRDataUrl && receiptNum && (
            <div className="divider"></div>
          )}

          {receiptNum && (
            <div className="text-center my-4" style={{ width: '100%' }}>
              <svg
                ref={barcodeRef}
                style={{ maxWidth: '100%', width: '100%', height: 'auto' }}
              ></svg>
            </div>
          )}

          <div className="footer">
            {receiptFooter.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </div>

        <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-3 mt-6`}>
          <button
            onClick={handlePrint}
            className={`flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center`}
          >
            <Printer size={16} className={isRTL ? 'ml-2' : 'mr-2'} />
            {t('pos.printReceipt')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export const printSessionReceipt = async (session, storeInfo = {}) => {
  const lang = typeof localStorage !== 'undefined' ? (localStorage.getItem('language') || 'en') : 'en';
  const t = getT();
  const storeName = storeInfo.storeName || defaultStoreName;
  const logoHtml = await receiptLogoHtml(storeInfo);
  const cashierName = session.cashier_name || t('settings.cashier');
  const registerName = session.register_name || t('sessions.register');
  const openingDate = session.start_time ? formatReceiptDateTime(session.start_time, lang) : 'N/A';

  const fmt = (v) => (v != null ? formatEGP(v) : 'EGP 0.00');
  const isRTL = lang === 'ar';
  const financialSummaryHtml = buildSessionFinancialSummaryHtml(session, t, fmt, lang);

  const html = `
    <html>
      <head>
        <title>Session Closing Receipt</title>
        ${ORDER_RECEIPT_FONT_LINK}
        <style>${SESSION_CLOSING_RECEIPT_PRINT_CSS}${SESSION_FINANCIAL_PRINT_CSS}</style>
      </head>
      <body ${sessionClosingReceiptBodyAttrs(isRTL)}>
        <div class="receipt">
          <div class="header">
            ${logoHtml}
            <div class="title">${storeName}</div>
            <div class="subtitle">${t('sessions.sessionClosingReceipt')}</div>
            <div class="subtitle">${openingDate}</div>
          </div>
          <div class="divider"></div>
          
          <div class="item">
            <span class="item-label">${t('sessions.cashier')}:</span>
            <span class="item-value">${cashierName}</span>
          </div>
          <div class="item">
            <span class="item-label">${t('sessions.register')}:</span>
            <span class="item-value">${registerName}</span>
          </div>
          <div class="item">
            <span class="item-label">${t('sessions.sessionNo')}:</span>
            <span class="item-value">${session.session_number ?? '—'}</span>
          </div>
          
          <div class="divider"></div>
          
          ${financialSummaryHtml}
        </div>
      </body>
    </html>
  `;

  printHtml(html);
};

/** Admin/manager session receipt: full session details for any status (open, closed, force_closed, reviewed) */
export const printSessionReceiptAdmin = async (session, storeInfo = {}) => {
  const t = getT();
  const storeName = storeInfo.storeName || defaultStoreName;
  const logoHtml = await receiptLogoHtml(storeInfo);
  const cashierName = session.cashier_name || t('settings.cashier');
  const registerName = session.register_name || t('sessions.register');

  const fmt = (v) => (v != null && v !== '' ? formatEGP(v) : '—');
  const lang = typeof localStorage !== 'undefined' ? (localStorage.getItem('language') || 'en') : 'en';
  const isRTL = lang === 'ar';
  const financialSummaryHtml = buildSessionFinancialSummaryHtml(session, t, fmt, lang);
  const fontLink = isRTL ? '<link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap" rel="stylesheet">' : '';

  const getStatusText = (status) => {
    if (status === 'open') return t('sessions.open');
    if (status === 'closed') return t('sessions.closed');
    if (status === 'force_closed') return t('sessions.forceClosed');
    return t('sessions.reviewed');
  };

  const html = `
    <html>
      <head>
        <title>Session Details</title>
        ${fontLink}
        <style>
          @page { size: 80mm auto; margin: 0; }
          body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding: 0; word-break: break-word; overflow-wrap: break-word; }
          .receipt { width: 100%; box-sizing: border-box; padding: 4px 16px; }
          .header { text-align: center; margin-bottom: 16px; }
          .title { font-size: 15px; font-weight: bold; margin-bottom: 4px; }
          .subtitle { font-size: 10px; color: #000; font-weight: 600; }
          .divider { border-top: 1px dashed #000; margin: 8px 0; }
          .item { display: flex; justify-content: space-between; margin: 4px 0; }
          .item-label { flex: 1; min-width: 0; }
          .item-value { text-align: right; font-weight: bold; }
          .section-title { font-weight: bold; margin-top: 8px; margin-bottom: 4px; font-size: 10px; }
          table { width: 100%; border-collapse: collapse; font-size: 10px; }
          th, td { padding: 3px 0; text-align: left; }
          th:last-child, td:last-child, th:nth-child(2), td:nth-child(2), th:nth-child(3), td:nth-child(3), th:nth-child(4), td:nth-child(4) { text-align: right; }
          .status-badge {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 1000;
            background: #000;
            color: #fff;
            margin-top: 4px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @media print { body { margin: 0; padding: 0; } }
          ${SESSION_FINANCIAL_PRINT_CSS}
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            ${logoHtml}
            <div class="title">${storeName}</div>
            <span class="status-badge">${getStatusText(session.status)}</span>
          </div>
          <div class="divider"></div>

          <div class="item">
            <span class="item-label">${t('sessions.cashier')}:</span>
            <span class="item-value">${cashierName}</span>
          </div>
          <div class="item">
            <span class="item-label">${t('sessions.register')}:</span>
            <span class="item-value">${registerName}</span>
          </div>
          <div class="item">
            <span class="item-label">${t('sessions.sessionNo')}:</span>
            <span class="item-value">${session.session_number ?? '—'}</span>
          </div>
          <div class="divider"></div>

          ${financialSummaryHtml}
          <div class="divider"></div>

          ${(session.closed_by_name || session.reviewed_by_name || session.notes || session.reviewed_at || session.reviewed_notes) ? `
          <div class="section-title">${t('common.actions')} & ${t('sessions.notes')}</div>
          ${session.closed_by_name ? `<div class="item"><span class="item-label">${t('sessions.closeSession')}:</span><span class="item-value">${session.closed_by_name}</span></div>` : ''}
          ${session.reviewed_by_name ? `<div class="item"><span class="item-label">${t('sessions.reviewedBy')}:</span><span class="item-value">${session.reviewed_by_name}</span></div>` : ''}
          ${session.reviewed_at ? `<div class="item"><span class="item-label">${t('sessions.reviewed')}:</span><span class="item-value">${new Date(session.reviewed_at).toLocaleString()}</span></div>` : ''}
          ${session.notes ? `<div class="item"><span class="item-label">${t('sessions.notes')}:</span><span class="item-value">${session.notes}</span></div>` : ''}
          ${session.reviewed_notes ? `<div class="item"><span class="item-label">${t('sessions.reviewNotes')}:</span><span class="item-value">${session.reviewed_notes}</span></div>` : ''}
          <div class="divider"></div>
          ` : ''}
        </div>
      </body>
    </html>
  `;

  const htmlWithDir = isRTL ? html.replace('<body>', '<body dir="rtl" style="text-align: right; font-family: \'Cairo\', \'Courier New\', monospace;">') : html;

  printHtml(htmlWithDir);
};

export default Receipt;
